import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import {
    Box,
    Input,
    InputGroup,
    InputRightElement,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    HStack,
    useToast,
    Spinner,
    IconButton,
    Badge,
} from "@chakra-ui/react";
import { FaEdit, FaSearch, FaTimesCircle } from "react-icons/fa";

const UserGroups = () => {
    const navigate = useNavigate();
    const toast = useToast();

    // Function to run when group is clicked. Modify to point to specific group
    const handleView = (id) => {
        console.log(`Edit button clicked for user group with ID: ${id}`);
        navigate(`/userGroupDetails/${id}`);
    };

    const [userGroups, setUserGroups] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const itemsPerPage = 8;

    const [filters, setFilters] = useState({ active_yn: null });
    const [searchTerm, setSearchTerm] = useState("");
    const [searchString, setSearchString] = useState("");

    const fetchUserGroups = async (page) => {
        setLoading(true);
        try {
            const response = await axiosInstance.post(
                `/dummy/get-user-groups`,
                {
                    searchString,
                    activeYN: filters.active_yn,
                    itemsPerPage,
                    currentPage: page,
                }
            );
            const usergroups = response.data;
            setUserGroups(usergroups.users);
            setTotalCount(usergroups.length);

            toast({
                title: "User groups loaded successfully!",
                description: "",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error fetching user groups:", error);
            toast({
                title: "Failed to load user groups",
                description: "Technical error occurred",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserGroups(currentPage);
    }, [currentPage, filters, searchString]);

    const handleSearch = () => {
        setCurrentPage(1);
        setSearchString(searchTerm);
    };

    const clearSearch = () => {
        setSearchTerm("");
        setSearchString("");
        fetchUserGroups(1);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        } else if (e.key === "Escape") {
            clearSearch();
        }
    };

    const handleFilterToggle = (filter) => {
        setFilters((prevFilters) => {
            let newValue = null;
            if (prevFilters[filter] === null) {
                newValue = true;
            } else if (prevFilters[filter] === true) {
                newValue = false;
            }
            return {
                ...prevFilters,
                [filter]: newValue,
            };
        });
    };

    const pagesCount = Math.ceil(totalCount / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const renderPageButtons = () => {
        const pageButtons = [];
        const maxButtons = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        const endPage = Math.min(pagesCount, startPage + maxButtons - 1);

        if (startPage > 1) {
            pageButtons.push(
                <Button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    isActive={1 === currentPage}
                >
                    1
                </Button>
            );
            if (startPage > 2) {
                pageButtons.push(
                    <Button key="ellipsis-start" disabled>
                        ...
                    </Button>
                );
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pageButtons.push(
                <Button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    isActive={i === currentPage}
                >
                    {i}
                </Button>
            );
        }

        if (endPage < pagesCount) {
            if (endPage < pagesCount - 1) {
                pageButtons.push(
                    <Button key="ellipsis-end" disabled>
                        ...
                    </Button>
                );
            }
            pageButtons.push(
                <Button
                    key={pagesCount}
                    onClick={() => handlePageChange(pagesCount)}
                    isActive={pagesCount === currentPage}
                >
                    {pagesCount}
                </Button>
            );
        }
        return pageButtons;
    };

    return (
        <Box overflowX="auto">
            <InputGroup mb={4} width="300px">
                <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <InputRightElement>
                    {searchTerm && (
                        <IconButton
                            icon={<FaTimesCircle />}
                            size="sm"
                            onClick={clearSearch}
                            aria-label="Clear search"
                            mr={2}
                        />
                    )}
                    <IconButton
                        icon={<FaSearch />}
                        size="sm"
                        onClick={handleSearch}
                        aria-label="Search"
                    />
                </InputRightElement>
            </InputGroup>
            {loading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100px"
                >
                    <Spinner size="xl" />
                </Box>
            ) : (
                <>
                    <Table variant="striped">
                        <Thead>
                            <Tr>
                                <Th>Group ID</Th>
                                <Th>Group Type</Th>
                                <Th>Name</Th>
                                <Th>Private YN</Th>
                                <Th width="10%">View</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {userGroups.map((row) => (
                                <Tr key={row.h_usergroup_id}>
                                    <Td>{row.h_usergroup_id}</Td>
                                    <Td>{row.group_type}</Td>
                                    <Td>{row.short_desc}</Td>
                                    <Td>
                                        <Badge colorScheme={row.private_yn ? 'red' : 'green'}>
                                            {row.private_yn ? 'Yes' : 'No'}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <Button
                                            onClick={() => {
                                                handleView(row.h_usergroup_id);
                                            }}
                                            leftIcon={<FaEdit />}
                                        >
                                            View
                                        </Button>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                    <HStack justifyContent="center" mt={4}>
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </Button>
                        {renderPageButtons()}
                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagesCount}
                        >
                            Next
                        </Button>
                    </HStack>
                </>
            )}
        </Box>
    );
};

export default UserGroups;
